import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
  IconButton,
  Container,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  Fade,
  Grow,
  Zoom,
  alpha,
  useTheme,
  keyframes
} from '@mui/material';
import {
  InfoOutlined as InfoOutlinedIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Whatshot as FireIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  WorkspacePremium as MedalIcon
} from '@mui/icons-material';
import { db } from '../firebase';
import { doc, collection, getDocs, query, where, collectionGroup, orderBy, onSnapshot } from 'firebase/firestore';

// Animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Leaderboard = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const contestId = searchParams.get('contestId');
  const providedPassword = searchParams.get('pw');

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [contest, setContest] = useState(null);
  const [employeeData, setEmployeeData] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [tagMap, setTagMap] = useState({});
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [animateRows, setAnimateRows] = useState(false);

  useEffect(() => {
    // No direct action until we have contest and password
  }, [contestId, providedPassword]);

  // Listen to contest doc in real-time
  useEffect(() => {
    if (!contestId || !providedPassword) {
      if (!contestId || !providedPassword) {
        setErrorMsg('Missing contestId or password in the URL.');
        setLoading(false);
      }
      return;
    }

    const contestRef = doc(db, 'contests', contestId);
    const unsubContest = onSnapshot(contestRef, (snapshot) => {
      if (!snapshot.exists()) {
        setErrorMsg('Contest not found.');
        setLoading(false);
        return;
      }

      const contestData = snapshot.data();
      // Check hashed password
      if (contestData.hashedPassword !== providedPassword) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // If access is granted
      setContest(contestData);
      setAccessDenied(false);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching contest:', error);
      setErrorMsg('Failed to load contest. Please try again.');
      setLoading(false);
    });

    return () => {
      unsubContest();
    };
  }, [contestId, providedPassword]);

  useEffect(() => {
    let unsubResponses;
    if (contest && !accessDenied) {
      setLoading(true);

      const fetchData = async () => {
        try {
          // Determine client IDs
          let clientIds = [];
          if (Array.isArray(contest.clientIds)) {
            clientIds = contest.clientIds;
          } else if (contest.clientId) {
            clientIds = [contest.clientId];
          }

          if (clientIds.length === 0) {
            setErrorMsg('No clients associated with this contest.');
            setLoading(false);
            return;
          }

          // Build a combined tagMap from all clients
          const tmpTagMap = {};
          for (const cid of clientIds) {
            const tagsRef = collection(db, 'clients', cid, 'nfcTags');
            const tagsQ = query(tagsRef, where('placement', '==', 'Person'));
            const tagsSnap = await getDocs(tagsQ);

            tagsSnap.forEach(docSnap => {
              const data = docSnap.data();
              if (data.assignedTo && data.id) {
                tmpTagMap[data.id] = data.assignedTo;
              }
            });
          }

          setTagMap(tmpTagMap);

          const employees = Array.from(new Set(Object.values(tmpTagMap)));
          const ignoreList = contest.ignoreList || [];
          const filtered = employees.filter(emp => !ignoreList.includes(emp));
          setFilteredEmployees(filtered);

          // Listen to responses in real-time
          const start = contest.startDate.toDate();
          const end = contest.endDate.toDate();

          const responsesRef = collectionGroup(db, 'responses');

          const combinedResponses = [];
          for (const cid of clientIds) {
            const responsesQ = query(
              responsesRef,
              where('clientId', '==', cid),
              where('timestamp', '>=', start),
              where('timestamp', '<=', end),
              orderBy('timestamp', 'asc')
            );
            const respSnap = await getDocs(responsesQ);
            respSnap.forEach(respDoc => combinedResponses.push(respDoc));
          }

          // Now process combinedResponses
          const counts = {};
          filtered.forEach(emp => (counts[emp] = 0));

          combinedResponses.forEach(respDoc => {
            const data = respDoc.data();
            const parentRef = respDoc.ref.parent.parent;
            const tagId = parentRef ? parentRef.id : null;

            if (tagId && tmpTagMap[tagId]) {
              const empName = tmpTagMap[tagId];
              if (counts.hasOwnProperty(empName)) {
                counts[empName] += 1;
              }
            }
          });

          // Sort employees by count descending
          const sortedEmployees = Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({ name, count }));

          setEmployeeData(sortedEmployees);
          setLoading(false);
          
          // Trigger animation after data loads
          setTimeout(() => setAnimateRows(true), 100);

        } catch (error) {
          console.error('Error fetching employees or responses:', error);
          setErrorMsg('Failed to load employees. Please try again.');
          setLoading(false);
        }
      };

      fetchData();
    }

    return () => {
      if (unsubResponses) unsubResponses();
    };
  }, [contest, accessDenied]);

  const getRankIcon = (rank) => {
    switch(rank) {
      case 1:
        return <TrophyIcon sx={{ color: '#FFD700', fontSize: 28 }} />;
      case 2:
        return <MedalIcon sx={{ color: '#C0C0C0', fontSize: 26 }} />;
      case 3:
        return <MedalIcon sx={{ color: '#CD7F32', fontSize: 24 }} />;
      default:
        return null;
    }
  };

  const getRankStyle = (rank) => {
    switch(rank) {
      case 1:
        return {
          background: `linear-gradient(135deg, ${alpha('#FFD700', 0.15)} 0%, ${alpha('#FFA500', 0.15)} 100%)`,
          borderLeft: '4px solid #FFD700',
          animation: `${pulse} 2s ease-in-out infinite`
        };
      case 2:
        return {
          background: `linear-gradient(135deg, ${alpha('#C0C0C0', 0.1)} 0%, ${alpha('#A0A0A0', 0.1)} 100%)`,
          borderLeft: '4px solid #C0C0C0'
        };
      case 3:
        return {
          background: `linear-gradient(135deg, ${alpha('#CD7F32', 0.1)} 0%, ${alpha('#A0522D', 0.1)} 100%)`,
          borderLeft: '4px solid #CD7F32'
        };
      default:
        return {
          borderLeft: `4px solid ${theme.palette.divider}`
        };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'white' }}>
          Loading Leaderboard...
        </Typography>
      </Box>
    );
  }

  if (accessDenied) {
    return (
      <Box sx={{ 
        p: 4, 
        textAlign: 'center', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
      }}>
        <Typography variant="h3" sx={{ mb: 2, color: 'white', fontWeight: 'bold' }}>
          Access Denied
        </Typography>
        <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
          The provided password does not match the contest's leaderboard.
        </Typography>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box sx={{ 
        p: 4, 
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: theme.palette.grey[100]
      }}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>{errorMsg}</Alert>
      </Box>
    );
  }

  if (!contest) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', minHeight: '100vh' }}>
        <Typography variant="body1">No contest found.</Typography>
      </Box>
    );
  }

  const maxCount = Math.max(...employeeData.map(emp => emp.count), 1);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.background.default} 400px)`,
      pb: 4
    }}>
      {/* Header Section */}
      <Box sx={{ 
        pt: 4, 
        pb: 8, 
        color: 'white',
        textAlign: 'center'
      }}>
        <Container maxWidth="lg">
          <Fade in timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 1 }}>
                <TrophyIcon sx={{ fontSize: 48, color: '#FFD700' }} />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontFamily: 'Inter, Montserrat, sans-serif',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  {contest.title}
                </Typography>
                {contest.description && (
                  <Tooltip title={contest.description} arrow>
                    <IconButton size="small" sx={{ color: 'white', ml: 1 }}>
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
                <TimeIcon sx={{ opacity: 0.9 }} />
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {contest.startDate.toDate().toLocaleDateString()} - {contest.endDate.toDate().toLocaleDateString()}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                <Chip 
                  icon={<PersonIcon />}
                  label={`${employeeData.length} Participants`}
                  sx={{ 
                    backgroundColor: alpha('#fff', 0.2),
                    color: 'white',
                    fontSize: '1rem',
                    py: 2,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Chip 
                  icon={<FireIcon />}
                  label={`${employeeData.reduce((sum, emp) => sum + emp.count, 0)} Total Responses`}
                  sx={{ 
                    backgroundColor: alpha('#fff', 0.2),
                    color: 'white',
                    fontSize: '1rem',
                    py: 2,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Leaderboard Content */}
      <Container maxWidth="lg" sx={{ mt: -4 }}>
        {employeeData.length === 0 ? (
          <Card sx={{ 
            textAlign: 'center', 
            py: 8,
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <PersonIcon sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h5" sx={{ color: theme.palette.text.secondary }}>
              No data available yet
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.disabled, mt: 1 }}>
              Participants will appear here once responses are recorded
            </Typography>
          </Card>
        ) : (
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            overflow: 'visible'
          }}>
            {/* Top 3 Podium */}
            {employeeData.length >= 3 && (
              <Box sx={{ 
                p: 4, 
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                <Typography variant="h5" sx={{ 
                  textAlign: 'center', 
                  mb: 3, 
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>
                  🏆 Top Performers 🏆
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'flex-end',
                  gap: 3,
                  flexWrap: 'wrap'
                }}>
                  {/* Second Place */}
                  <Zoom in={animateRows} style={{ transitionDelay: '200ms' }}>
                    <Card sx={{ 
                      textAlign: 'center', 
                      p: 3,
                      minWidth: 180,
                      background: `linear-gradient(135deg, ${alpha('#C0C0C0', 0.1)} 0%, ${alpha('#A0A0A0', 0.1)} 100%)`,
                      border: '2px solid #C0C0C0',
                      transform: 'translateY(20px)'
                    }}>
                      <MedalIcon sx={{ fontSize: 60, color: '#C0C0C0', mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {employeeData[1].name}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#C0C0C0' }}>
                        {employeeData[1].count}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        responses
                      </Typography>
                    </Card>
                  </Zoom>

                  {/* First Place */}
                  <Zoom in={animateRows} style={{ transitionDelay: '100ms' }}>
                    <Card sx={{ 
                      textAlign: 'center', 
                      p: 3,
                      minWidth: 200,
                      background: `linear-gradient(135deg, ${alpha('#FFD700', 0.15)} 0%, ${alpha('#FFA500', 0.15)} 100%)`,
                      border: '3px solid #FFD700',
                      boxShadow: '0 8px 32px rgba(255, 215, 0, 0.3)',
                      animation: `${pulse} 2s ease-in-out infinite`
                    }}>
                      <TrophyIcon sx={{ fontSize: 80, color: '#FFD700', mb: 1 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {employeeData[0].name}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                        {employeeData[0].count}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        responses
                      </Typography>
                    </Card>
                  </Zoom>

                  {/* Third Place */}
                  <Zoom in={animateRows} style={{ transitionDelay: '300ms' }}>
                    <Card sx={{ 
                      textAlign: 'center', 
                      p: 3,
                      minWidth: 180,
                      background: `linear-gradient(135deg, ${alpha('#CD7F32', 0.1)} 0%, ${alpha('#A0522D', 0.1)} 100%)`,
                      border: '2px solid #CD7F32',
                      transform: 'translateY(40px)'
                    }}>
                      <MedalIcon sx={{ fontSize: 50, color: '#CD7F32', mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {employeeData[2].name}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#CD7F32' }}>
                        {employeeData[2].count}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        responses
                      </Typography>
                    </Card>
                  </Zoom>
                </Box>
              </Box>
            )}

            {/* Full Rankings Table */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Complete Rankings
              </Typography>
              {employeeData.map((emp, index) => (
                <Grow 
                  in={animateRows} 
                  key={emp.name}
                  style={{ 
                    transformOrigin: '0 0 0',
                    transitionDelay: `${Math.min(index * 50, 1000)}ms`
                  }}
                >
                  <Card
                    sx={{
                      mb: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      ...getRankStyle(index + 1),
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Rank */}
                      <Box sx={{ 
                        minWidth: 60,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        {getRankIcon(index + 1)}
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: index < 3 ? theme.palette.text.primary : theme.palette.text.secondary
                          }}
                        >
                          #{index + 1}
                        </Typography>
                      </Box>

                      {/* Name */}
                      <Avatar 
                        sx={{ 
                          bgcolor: index === 0 ? '#FFD700' : 
                                  index === 1 ? '#C0C0C0' : 
                                  index === 2 ? '#CD7F32' : 
                                  theme.palette.primary.main,
                          width: 44,
                          height: 44,
                          fontWeight: 600
                        }}
                      >
                        {emp.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {emp.name}
                      </Typography>
                    </Box>

                    {/* Score and Progress */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 3,
                      minWidth: 300
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(emp.count / maxCount) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: index === 0 ? 
                                `linear-gradient(90deg, #FFD700 0%, #FFA500 100%)` :
                                `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          {emp.count}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          responses
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grow>
              ))}
            </Box>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default Leaderboard;